import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import CampoNumero from './CampoNumero';
import { nombreOrigenRegistro } from '../utils/origenRegistro';
import {
  PLANES_NK, USOS_IA, SUPUESTOS_INICIALES, NOMBRES_FIJOS, FUNCIONES, MESES_NK,
  porPlan, mes, repartir, proyeccion, resumenMezcla, publicidad, cac, totalFijos, costoIA, combinar,
} from '../utils/numerosKontrol';

// 📊 Números de Kontrol: SOLO la admin (la base rechaza a cualquier otra cuenta).
// Datos reales del mes + calculadora de costos, ganancia por plan y 12 meses.
const CLAVE = 'kontrol.numerosKontrol.v2'; // v2: cupos por plan de sql/130
const L = (x) => { const r = Math.round(Number(x) || 0); return `${r < 0 ? '−' : ''}L ${Math.abs(r).toLocaleString('es-HN')}`; };
const pct = (x) => `${Math.round((Number(x) || 0) * 100)}%`;
const color = (x) => (x < 0 ? '#B91C1C' : '#047857');
const th = { padding: '8px 10px', fontSize: '12px', fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap', background: '#F5F3FF', color: '#4C1D95' };
const td = { padding: '8px 10px', fontSize: '13px', textAlign: 'right', whiteSpace: 'nowrap', borderTop: '1px solid #F1EDF9', fontVariantNumeric: 'tabular-nums' };
const tdL = { ...td, textAlign: 'left', whiteSpace: 'normal', color: '#4B5563' };
const card = { background: 'white', border: '1px solid #EEE9F8', borderRadius: '14px', padding: '16px', marginBottom: '14px' };
const ESTADOS = { activa: 'Pagando', prueba: 'En prueba', cortesia: 'Cortesía', pausada: 'Pausadas', vencida: 'Vencidas' };

function leer() {
  try { return combinar(SUPUESTOS_INICIALES, JSON.parse(localStorage.getItem(CLAVE) || 'null')); } catch { return SUPUESTOS_INICIALES; }
}

export default function NumerosKontrolView({ onVolver }) {
  const [s, setS] = useState(leer);
  const [real, setReal] = useState(null);
  const [origenes, setOrigenes] = useState(null);
  const [error, setError] = useState(null);
  const [vista, setVista] = useState('uso'); // uso | tope | fundador

  useEffect(() => { try { localStorage.setItem(CLAVE, JSON.stringify(s)); } catch { /* sin almacenamiento */ } }, [s]);
  useEffect(() => {
    supabase.rpc('admin_numeros_kontrol').then(({ data, error: e }) => {
      if (e) setError(String(e.message || '').includes('NO_AUTORIZADO') ? 'Solo la dueña de Kontrol puede ver esto.' : 'No se pudieron cargar los datos reales.');
      else setReal(data);
    });
    // De dónde llegó cada cuenta (?o= de los enlaces, sql/141).
    supabase.rpc('admin_registros_por_origen').then(({ data }) => setOrigenes(data || []));
  }, []);

  // Cambia un valor por ruta: ['uso','emp','fotos'].
  const set = (ruta) => (v) => setS((x) => {
    const copia = JSON.parse(JSON.stringify(x));
    let o = copia; ruta.slice(0, -1).forEach((k) => { o = o[k]; });
    o[ruta[ruta.length - 1]] = v;
    return copia;
  });
  const campo = (ruta, { prefijo, sufijo, decimales = 0, ancho = 92 } = {}) => {
    let v = s; ruta.forEach((k) => { v = v?.[k]; });
    return <CampoNumero valor={String(v ?? '')} onChange={set(ruta)} prefijo={prefijo} sufijo={sufijo} decimales={decimales} min={0}
      estilo={{ width: `${ancho}px`, padding: '6px 8px', border: '1px solid #DDD6FE', borderRadius: '8px', fontSize: '13px', textAlign: 'right' }} />;
  };

  // Datos reales → clientes de hoy y cortesías.
  const conteoReal = useMemo(() => {
    const c = {};
    (real?.suscripciones || []).forEach((x) => { c[x.estado] = (c[x.estado] || 0) + Number(x.n || 0); });
    return c;
  }, [real]);
  const usarReales = () => setS((x) => ({ ...x, hoy: conteoReal.activa || 0, cortesias: conteoReal.cortesia || 0 }));

  const costoIAReal = real ? costoIA(real.ia, s) : 0;
  const planes = porPlan(s, vista === 'fundador' ? { fundador: true } : { uso: vista === 'tope' ? 'tope' : 'uso' });
  const mezcla = resumenMezcla(s);
  const pub = publicidad(s);
  const proy = proyeccion(s);
  const escenarios = [...s.escenarios.map((t, i) => ({ nombre: `${Number(t) || 0} clientes`, i, r: mes(repartir(Number(t) || 0, s), s) }))];
  const fijos = totalFijos(s);
  const ult = proy.filas[11];
  const topePro = porPlan(s, { uso: 'tope' });

  if (error) return <div className="k-page"><p style={{ color: '#991B1B', fontWeight: 700 }}>⚠️ {error}</p><button className="k-btn-soft" onClick={onVolver}>← Volver</button></div>;

  return (
    <div className="k-page" style={{ maxWidth: '1100px' }}>
      <div className="k-page-head">
        <h2 className="k-page-title">📊 Números de Kontrol</h2>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="k-btn-ghost" onClick={() => setS(SUPUESTOS_INICIALES)}>↺ Valores iniciales</button>
          <button onClick={onVolver} className="k-btn-soft">← Volver</button>
        </div>
      </div>
      <p style={{ margin: '0 0 14px', color: '#6B7280', fontSize: '13px' }}>Solo tú ves esta pantalla. Cambia cualquier número y todo se recalcula. Lo que cambies se guarda en este equipo.</p>

      {/* Indicadores */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px', marginBottom: '14px' }}>
        {[
          ['Te queda al mes', L(ult.gan), `en ${ult.mes} con ${Math.round(ult.clientes)} clientes (techo ${Math.round(proy.techo)})`, true],
          ['Te cuesta cada cliente', L(cac(s)), `${proy.nuevos.toFixed(1)} clientes nuevos al mes con tus anuncios`],
          ['Cubres los gastos fijos con', mezcla.equilibrio ? `${mezcla.equilibrio} clientes` : '—', `gastos fijos ${L(fijos)} al mes`],
          ['Colchón que necesitas', L(proy.colchon), proy.equilibrio === null ? 'no llegas a ganar en 12 meses' : proy.equilibrio === 0 ? 'ganas desde el primer mes' : `empiezas a ganar en ${MESES_NK[proy.equilibrio]}`],
        ].map(([t, v, d, hero]) => (
          <div key={t} style={{ ...card, marginBottom: 0, background: hero ? 'linear-gradient(135deg,#6D28D9,#DB2777)' : 'white', color: hero ? 'white' : '#1F1A2E' }}>
            <p style={{ margin: 0, fontSize: '11px', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', opacity: 0.85 }}>{t}</p>
            <p style={{ margin: '2px 0', fontSize: '24px', fontWeight: 800 }}>{v}</p>
            <p style={{ margin: 0, fontSize: '12px', opacity: 0.85 }}>{d}</p>
          </div>
        ))}
      </div>

      {/* Datos reales */}
      <section style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>🔴 Hoy en Kontrol (datos reales{real ? ` · ${real.mes}` : ''})</h3>
          {real && <button className="k-btn-ghost" onClick={usarReales}>Usar estos clientes en la proyección</button>}
        </div>
        {!real ? <p style={{ color: '#6B7280' }}>⏳ Cargando…</p> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginTop: '10px', fontSize: '13px' }}>
            <div>
              <b>Cuentas</b>
              <p style={{ margin: '4px 0' }}>{Object.entries(ESTADOS).map(([k, t]) => `${t}: ${conteoReal[k] || 0}`).join(' · ')}</p>
              <p style={{ margin: 0, color: '#6B7280' }}>{real.negocios} negocios creados · {real.negocios_vendiendo} vendieron en 30 días · {real.pedidos} pedidos del catálogo este mes</p>
            </div>
            <div>
              <b>IA usada este mes (todas las cuentas)</b>
              <p style={{ margin: '4px 0' }}>📸 {real.ia.fotos} fotos ({real.ia.fotos_fallidas} fallidas) · ✍️ {real.ia.descripciones} descripciones · 💡 {real.ia.ideas} ideas · 🧩 {real.ia.materiales} publicaciones ({real.ia.premium} premium) · 🎯 {real.ia.planMes + real.ia.planDia} planes de metas</p>
              <p style={{ margin: 0, fontWeight: 700, color: '#6D28D9' }}>Costo aproximado: {L(costoIAReal)} (US$ {(costoIAReal / s.tasa).toFixed(2)})</p>
            </div>
            <div>
              <b>Servidor</b>
              <p style={{ margin: '4px 0' }}>{Object.entries(real.storage_mb || {}).map(([b, mb]) => `${b}: ${Number(mb).toLocaleString('es-HN')} MB`).join(' · ')}</p>
              <p style={{ margin: 0, color: '#6B7280' }}>Total {(Object.values(real.storage_mb || {}).reduce((a, b) => a + Number(b), 0) / 1024).toFixed(1)} GB de 100 GB incluidos en Supabase Pro · {real.publicaciones} publicaciones en redes este mes</p>
            </div>
          </div>
        )}
      </section>

      {/* De dónde llegan las cuentas */}
      <section style={card}>
        <h3 style={{ margin: 0, fontSize: '16px' }}>📲 ¿De dónde llegan las cuentas nuevas?</h3>
        <p style={{ margin: '4px 0 10px', fontSize: '12.5px', color: '#6B7280' }}>Sale del enlace con que entraron (?o=instagram, ?o=tiktok, botones de la web…). Las cuentas creadas antes del 28-oct quedan en "Sin etiqueta".</p>
        {!origenes ? <p style={{ color: '#6B7280' }}>⏳ Cargando…</p> : !origenes.length ? <p style={{ color: '#6B7280' }}>Todavía no hay cuentas.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ textAlign: 'left', color: '#6B7280' }}>{['Origen', 'Cuentas', 'Últimos 30 días', 'En prueba', 'Pagando', 'Ya vendieron'].map((t) => <th key={t} style={{ padding: '6px 8px', borderBottom: '1px solid #EDE9FE', fontWeight: 700 }}>{t}</th>)}</tr></thead>
              <tbody>{origenes.map((o) => (
                <tr key={o.origen}>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #F5F3FF' }}>{nombreOrigenRegistro(o.origen)}</td>
                  {[o.total, o.ultimos_30, o.prueba, o.pagando, o.vendieron].map((v, i) => <td key={i} style={{ padding: '6px 8px', borderBottom: '1px solid #F5F3FF', fontWeight: i === 3 && v ? 800 : 400, color: i === 3 && v ? '#047857' : undefined }}>{v}</td>)}
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      {/* Por plan */}
      <section style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>💵 Cuánto te deja cada cliente al mes</h3>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[['uso', 'Uso típico'], ['tope', 'Uso al máximo'], ['fundador', 'Precio fundador']].map(([k, t]) => (
              <button key={k} onClick={() => setVista(k)} style={{ padding: '6px 12px', borderRadius: '999px', border: '1px solid #DDD6FE', background: vista === k ? '#6D28D9' : 'white', color: vista === k ? 'white' : '#5B21B6', fontWeight: 700, cursor: 'pointer' }}>{t}</button>
            ))}
          </div>
        </div>
        <div style={{ overflowX: 'auto', marginTop: '10px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '560px' }}>
            <thead><tr><th style={{ ...th, textAlign: 'left' }}>Por cliente</th>{planes.map((p) => <th key={p.k} style={th}>{p.nombre}</th>)}</tr></thead>
            <tbody>
              <tr><td style={tdL}>Precio</td>{planes.map((p) => <td key={p.k} style={td}>{L(p.precio)}</td>)}</tr>
              <tr><td style={tdL}>ISV 15% incluido</td>{planes.map((p) => <td key={p.k} style={{ ...td, color: '#B91C1C' }}>{L(-p.isv)}</td>)}</tr>
              <tr><td style={tdL}>Pasarela ({s.pasarela.parte}% paga con tarjeta)</td>{planes.map((p) => <td key={p.k} style={{ ...td, color: '#B91C1C' }}>{L(-p.pasarela)}</td>)}</tr>
              <tr><td style={tdL}>IA (fotos, textos, contenido, metas)</td>{planes.map((p) => <td key={p.k} style={{ ...td, color: '#B91C1C' }}>{L(-p.ia)}</td>)}</tr>
              <tr><td style={tdL}>Servidor (si pasas lo incluido)</td>{planes.map((p) => <td key={p.k} style={{ ...td, color: '#B91C1C' }}>{L(-p.servidor)}</td>)}</tr>
              <tr><td style={{ ...tdL, fontWeight: 800, color: '#1F1A2E' }}>Te queda</td>{planes.map((p) => <td key={p.k} style={{ ...td, fontWeight: 800, color: color(p.neto) }}>{L(p.neto)} · {pct(p.margen)}</td>)}</tr>
            </tbody>
          </table>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6B7280' }}>
          Con tu mezcla ({Object.values(s.mezcla).join('/')}) el cliente promedio paga {L(mezcla.precio)} y te deja {L(mezcla.neto)} ({pct(mezcla.margen)}).
          {topePro[0].neto < 0 && <b style={{ color: '#B91C1C' }}> ⚠️ Un Emprende que use toda la IA te hace perder {L(-topePro[0].neto)} al mes.</b>}
        </p>
      </section>

      {/* Escenarios */}
      <section style={card}>
        <h3 style={{ margin: '0 0 6px', fontSize: '16px' }}>📈 Escenarios: ¿cuánto queda con X clientes que pagan?</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
          {s.escenarios.map((_, i) => <label key={i} style={{ fontSize: '12px', color: '#6B7280', display: 'grid', gap: '4px' }}>Escenario {i + 1} (clientes) {campo(['escenarios', i], { ancho: 110 })}</label>)}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '560px' }}>
            <thead><tr><th style={{ ...th, textAlign: 'left' }}>Al mes</th>{escenarios.map((e) => <th key={e.i} style={th}>{e.nombre}</th>)}</tr></thead>
            <tbody>
              {[
                ['Ingreso', (r) => L(r.ingreso)],
                ['ISV', (r) => L(-r.isv), true],
                ['Pasarela', (r) => L(-r.pasarela), true],
                [`IA (incluye ${s.cortesias} cortesías)`, (r) => L(-r.ia), true],
                ['Servidor extra', (r) => L(-r.servidor), true],
                ['Gastos fijos', (r) => L(-r.fijos), true],
                [`Anuncios para reponer a los que se van (${s.churn}%)`, (r) => L(-r.reponer), true],
              ].map(([t, f, neg]) => (
                <tr key={t}><td style={tdL}>{t}</td>{escenarios.map((e) => <td key={e.i} style={{ ...td, color: neg ? '#B91C1C' : '#1F1A2E' }}>{f(e.r)}</td>)}</tr>
              ))}
              <tr><td style={{ ...tdL, fontWeight: 800, color: '#1F1A2E' }}>Te queda al mes</td>{escenarios.map((e) => <td key={e.i} style={{ ...td, fontWeight: 800, color: color(e.r.queda), background: '#F5F3FF' }}>{L(e.r.queda)}</td>)}</tr>
              <tr><td style={tdL}>Al año</td>{escenarios.map((e) => <td key={e.i} style={{ ...td, color: color(e.r.queda) }}>{L(e.r.queda * 12)}</td>)}</tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Publicidad */}
      <section style={card}>
        <h3 style={{ margin: '0 0 6px', fontSize: '16px' }}>📣 ¿La publicidad te deja ganar?</h3>
        <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6B7280' }}>Cada cliente te cuesta {L(cac(s))} en anuncios ({L(s.cpl)} por registro ÷ {s.conv}% que paga). Sano = te deja 3 veces o más lo que costó.</p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {pub.map((p) => {
            const [c, b, t] = p.veces >= 3 ? ['#047857', '#ECFDF5', 'Sano'] : p.veces >= 1.5 ? ['#92400E', '#FEF3C7', 'Justo'] : ['#B91C1C', '#FEF2F2', 'Pierdes'];
            return (
              <div key={p.k} style={{ flex: '1 1 200px', background: b, borderRadius: '12px', padding: '10px 12px', fontSize: '13px' }}>
                <b>{p.nombre}</b> <span style={{ color: c, fontWeight: 800 }}>{p.veces.toFixed(1)}× · {t}</span>
                <p style={{ margin: '2px 0 0', color: '#4B5563' }}>Te deja {L(p.deja)} en {p.vida.toFixed(1)} meses · lo recuperas en {p.recuperaMeses ? `${p.recuperaMeses.toFixed(1)} meses` : 'nunca'}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 12 meses */}
      <section style={card}>
        <h3 style={{ margin: '0 0 6px', fontSize: '16px' }}>🗓️ Tus próximos 12 meses</h3>
        <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6B7280' }}>Empiezas con {s.hoy} clientes que pagan, inviertes {L(s.ads)} al mes en anuncios y se va el {s.churn}% cada mes.</p>
        <Grafico filas={proy.filas} />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '560px' }}>
            <thead><tr>{['Mes', 'Clientes', 'Ingreso', 'Costos + anuncios', 'Ganancia', 'Acumulado'].map((t, i) => <th key={t} style={{ ...th, textAlign: i ? 'right' : 'left' }}>{t}</th>)}</tr></thead>
            <tbody>
              {proy.filas.map((f) => (
                <tr key={f.mes}><td style={tdL}>{f.mes}</td><td style={td}>{Math.round(f.clientes)}</td><td style={td}>{L(f.ingreso)}</td><td style={{ ...td, color: '#B91C1C' }}>{L(-f.costos)}</td><td style={{ ...td, fontWeight: 700, color: color(f.gan) }}>{L(f.gan)}</td><td style={{ ...td, color: color(f.acum) }}>{L(f.acum)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Supuestos */}
      <section style={card}>
        <h3 style={{ margin: '0 0 10px', fontSize: '16px' }}>⚙️ Supuestos (cámbialos con tus datos reales)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', fontSize: '13px' }}>
          <Grupo titulo="Precios al mes (normal · fundador)">
            {PLANES_NK.map((p) => <Fila key={p.k} t={p.nombre}>{campo(['precios', p.k], { prefijo: 'L' })} {campo(['fundador', p.k], { prefijo: 'L' })}</Fila>)}
            <Fila t="Clientes con precio fundador">{campo(['nFund'])}</Fila>
            <Fila t="Dólar (lempiras por US$1)">{campo(['tasa'], { decimales: 2, prefijo: 'L' })}</Fila>
          </Grupo>
          <Grupo titulo="Qué plan eligen (%)">
            {PLANES_NK.map((p) => <Fila key={p.k} t={p.nombre}>{campo(['mezcla', p.k], { sufijo: '%' })}</Fila>)}
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><input type="checkbox" checked={s.isv} onChange={(e) => set(['isv'])(e.target.checked)} /> El precio incluye ISV 15%</label>
            <Fila t="Clientes que pagan con tarjeta">{campo(['pasarela', 'parte'], { sufijo: '%' })}</Fila>
            <Fila t="Comisión pasarela">{campo(['pasarela', 'pct'], { sufijo: '%', decimales: 1 })} {campo(['pasarela', 'fijo'], { prefijo: '$', decimales: 2 })}</Fila>
          </Grupo>
          <Grupo titulo="Gastos fijos al mes">
            {Object.keys(NOMBRES_FIJOS).map((k) => <Fila key={k} t={NOMBRES_FIJOS[k]}>{campo(['fijos', k], { prefijo: 'L' })}</Fila>)}
            <Fila t={<b>Total</b>}><b>{L(fijos)}</b></Fila>
          </Grupo>
          <Grupo titulo="Publicidad y clientes">
            <Fila t="Anuncios al mes">{campo(['ads'], { prefijo: 'L' })}</Fila>
            <Fila t="Costo por registro (Meta Ads)">{campo(['cpl'], { prefijo: 'L' })}</Fila>
            <Fila t="Pagan después de la prueba">{campo(['conv'], { sufijo: '%' })}</Fila>
            <Fila t="Se van cada mes">{campo(['churn'], { sufijo: '%' })}</Fila>
            <Fila t="Clientes que pagan hoy">{campo(['hoy'])}</Fila>
            <Fila t="Cuentas de cortesía">{campo(['cortesias'])}</Fila>
          </Grupo>
        </div>
        <div style={{ overflowX: 'auto', marginTop: '14px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '640px' }}>
            <thead><tr><th style={{ ...th, textAlign: 'left' }}>IA: costo por uso · uso típico al mes (tope)</th><th style={th}>US$ c/u</th>{PLANES_NK.map((p) => <th key={p.k} style={th}>{p.nombre}</th>)}</tr></thead>
            <tbody>
              {USOS_IA.map((u) => (
                <tr key={u.k}>
                  <td style={tdL}>{u.nombre}<br /><small style={{ color: '#9CA3AF' }}>{u.proveedor}</small></td>
                  <td style={td}>{campo(['unit', u.k], { prefijo: '$', decimales: 3, ancho: 84 })}</td>
                  {PLANES_NK.map((p) => <td key={p.k} style={td}>{campo(['uso', p.k, u.k], { ancho: 70 })} <small style={{ color: '#9CA3AF' }}>({s.tope[p.k][u.k]})</small></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Lo que ofrece Kontrol */}
      <section style={card}>
        <h3 style={{ margin: '0 0 10px', fontSize: '16px' }}>🧰 Lo que ofrece Kontrol y qué cuesta mantenerlo</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '10px' }}>
          {FUNCIONES.map((g) => (
            <div key={g.grupo} style={{ border: '1px solid #F1EDF9', borderRadius: '12px', padding: '10px 12px', fontSize: '13px' }}>
              <b>{g.grupo}</b> <span style={{ fontSize: '11px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px', background: g.costo === 'ia' ? '#FEF3C7' : '#ECFDF5', color: g.costo === 'ia' ? '#92400E' : '#047857' }}>{g.costo === 'ia' ? 'cuesta por uso' : 'incluido en el servidor'}</span>
              <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: '#4B5563' }}>{g.items.map((i) => <li key={i}>{i}</li>)}</ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const Grupo = ({ titulo, children }) => (
  <div style={{ display: 'grid', gap: '8px', alignContent: 'start' }}><b style={{ color: '#4C1D95' }}>{titulo}</b>{children}</div>
);
const Fila = ({ t, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}><span>{t}</span><span style={{ display: 'flex', gap: '6px' }}>{children}</span></div>
);

// Barras = ganancia del mes; línea = acumulado (tu caja).
function Grafico({ filas }) {
  const W = 720; const H = 220; const pl = 64; const pr = 12; const pt = 12; const pb = 26;
  const vals = filas.flatMap((f) => [f.gan, f.acum]).concat(0);
  let max = Math.max(...vals); let min = Math.min(...vals); if (max === min) max += 1;
  const y = (v) => pt + ((max - v) / (max - min)) * (H - pt - pb);
  const bw = (W - pl - pr) / filas.length;
  const ticks = [min, (min + max) / 2, max, 0];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', marginBottom: '8px' }} role="img" aria-label="Ganancia por mes y acumulado">
      {ticks.map((t, i) => (
        <g key={i}><line x1={pl} x2={W - pr} y1={y(t)} y2={y(t)} stroke={t === 0 ? '#9CA3AF' : '#EEE9F8'} />
          <text x={pl - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="#6B7280">{L(t)}</text></g>
      ))}
      {filas.map((f, i) => (
        <g key={f.mes}>
          <rect x={pl + i * bw + bw * 0.2} y={Math.min(y(f.gan), y(0))} width={bw * 0.6} height={Math.max(1, Math.abs(y(f.gan) - y(0)))} rx="3" fill={f.gan >= 0 ? '#059669' : '#DC2626'}><title>{`${f.mes}: ${L(f.gan)}`}</title></rect>
          <text x={pl + i * bw + bw / 2} y={H - 8} textAnchor="middle" fontSize="10" fill="#6B7280">{f.mes}</text>
        </g>
      ))}
      <polyline points={filas.map((f, i) => `${pl + i * bw + bw / 2},${y(f.acum)}`).join(' ')} fill="none" stroke="#6D28D9" strokeWidth="2.5" />
      {filas.map((f, i) => <circle key={f.mes} cx={pl + i * bw + bw / 2} cy={y(f.acum)} r="3" fill="#6D28D9"><title>{`Acumulado ${f.mes}: ${L(f.acum)}`}</title></circle>)}
    </svg>
  );
}
